import { set } from 'lodash';
import { ControllerParams, CreateControllerFn } from '@wix/yoshi-flow-editor';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { createSlotVeloAPIFactory } from '@wix/widget-plugins-ooi/velo';
import { getSettingsValues } from '@wix/tpa-settings';
import { ServiceType, TimezoneType } from '@wix/bookings-uou-types';
import { getTrackingInfoForServicePageLoads } from '@wix/bookings-analytics-adapter';
import { Service } from '@wix/ambassador-services-catalog-server/types';
import { Service as ServiceV2 } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  bookingsBookItClick,
  bookingsCantBookGroupsMessage,
  bookingsServicePageDrpodownChangeLocationSelection,
  bookingsServicesPageView,
} from '@wix/bi-logger-wixboost-ugc/v2';
import { getConfig } from '../../api/config.api';
import {
  dummyViewModelFactory as dummyViewModelFactoryDeprecated,
  ServicePageViewModel as ServicePageViewModelDeprecated,
  servicePageViewModelFactory as servicePageViewModelFactoryDeprecated,
} from '../../service-page-view-model-deprecated/servicePageViewModel';
import {
  dummySchedulingViewModel as dummySchedulingViewModelDeprecated,
  SchedulingSectionStatus,
  SchedulingSectionViewModel,
  schedulingSectionViewModelFactory,
} from '../../service-page-view-model-deprecated/scheduling-section-view-model/schedulingSectionViewModel';
import { schedulingLocationViewModelFactory } from '../../service-page-view-model-deprecated/scheduling-location-view-model/schedulingLocationViewModel';
import {
  SchedulingTimezoneViewModel,
  schedulingTimezoneViewModelFactoryDeprecated,
} from '../../service-page-view-model-deprecated/shceduling-timezone-view-model/schedulingTimezoneViewModel';
import { WidgetConfig } from '../../types/shared-types';
import { CourseAvailabilityDisplay } from '../../service-page-view-model-deprecated/details-section-view-model/detailsSectionViewModel';
import { initializeViewModels } from './controller-logic/initialize-view-models-deprecated';
import { SERVICE_PAGE_NAME } from './constants';
import { initUserMessage } from './controller-logic/init-user-message';
import { getServiceSchedulingData } from './controller-logic/scheduling-fetcher';
import { handleNavigation } from './controller-logic/handle-navigation-deprecated';
import { shouldRedirectToCalendarPage } from './controller-logic/ooi-migration';
import { ACTION_NAMES, biDefaults, generateWidgetDefaults } from './bi/consts';
import settingsParams from './settingsParams';
import { BookingsServicesPageViewParams } from './types';
import { navigateToBookingsCalendarPage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import {
  BookingsQueryParams,
  getServiceSlug,
  getUrlQueryParamValue,
  isBookingCalendarInstalled as isBookingCalendarInstalledUtils,
  isBookingFormInstalled as isBookingFormInstalledUtils,
  trackAnalytics,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { initializeWidget } from './controller-logic/init-widgets';
import { dummySchedulingViewModel } from '../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import { dummyViewModelFactory } from '../../service-page-view-model/servicePageViewModel';

// https://stackoverflow.com/questions/63961803/eslint-says-all-enums-in-typescript-app-are-already-declared-in-the-upper-scope
// eslint-disable-next-line no-shadow
enum ScheduleStatus {
  UNDEFINED = 'UNDEFINED',
  CREATED = 'CREATED',
  CANCELLED = 'CANCELLED',
}

export const WARMUP_DATA_KEY = 'ServicePageConfig_';

const createController: CreateControllerFn = async ({
  flowAPI,
}: ControllerParams) => {
  const setProps = flowAPI.controllerConfig.setProps;
  // FIXME: Yoshi missing translations issue workaround.
  // https://wix.slack.com/archives/CAL591CDV/p1652941335953659
  try {
    await flowAPI.translations.init();
  } catch {}

  const t = flowAPI.translations.t;
  const { controllerConfig, httpClient, experiments, reportError } = flowAPI;
  const publicData = controllerConfig.config.publicData.COMPONENT || {};
  const settings = getSettingsValues(publicData, settingsParams, {
    experiments,
  });
  let service: Service | ServiceV2 | undefined;
  let isEditorX: any;
  let prevSettings = settings;

  return {
    async pageReady() {
      const { platformAPIs, appParams, wixCodeApi } = controllerConfig;

      if (shouldRedirectToCalendarPage(controllerConfig.wixCodeApi)) {
        const serviceSlug = await getServiceSlug({
          wixCodeApi,
          pageName: SERVICE_PAGE_NAME,
          useCustomUrlSegments: experiments.enabled(
            'specs.bookings.useCustomUrlSegmentsForSlug',
          ),
        });

        navigateToBookingsCalendarPage(wixCodeApi, platformAPIs, {
          serviceSlugOrBasket: serviceSlug,
        });

        setProps({
          isRedirectingToCalendar: true,
        });
        return;
      }

      const isSEO = flowAPI.environment.isSEO;

      const isBookingCalendarInstalled = await isBookingCalendarInstalledUtils(
        wixCodeApi,
      );

      const isBookingFormInstalled = await isBookingFormInstalledUtils(
        wixCodeApi,
      );
      const { isPreview, isSSR, isEditor, isViewer } = flowAPI.environment;
      let scheduleViewModel: SchedulingSectionViewModel = {
        status: SchedulingSectionStatus.LOADING,
        isBookable: false,
      };
      let locationViewModel, getServiceSchedulingDataByLocation;
      let timezoneViewModel: SchedulingTimezoneViewModel;
      let viewModel: ServicePageViewModelDeprecated,
        navigateToCalendar = () => {};

      async function fetchWidgetConfigFromServer(serviceSlug) {
        return (
          await httpClient.request({
            ...getConfig(serviceSlug, isPreview),
            headers: { 'x-time-budget': '10000' },
          })
        ).data;
      }

      const initSlotIntegration = (serviceId?: string) => {
        const slotAPIFactory = createSlotVeloAPIFactory(controllerConfig);
        const slot1$w = slotAPIFactory.getSlotAPI('slot1');
        if (serviceId) {
          slot1$w.bookingsServiceId = serviceId;
        }
      };

      const initWidget = async () => {
        if (isViewer || isPreview) {
          const serviceSlug = await getServiceSlug({
            wixCodeApi,
            pageName: SERVICE_PAGE_NAME,
            useCustomUrlSegments: experiments.enabled(
              'specs.bookings.useCustomUrlSegmentsForSlug',
            ),
          });

          let config: WidgetConfig;

          try {
            config = await fetchWidgetConfigFromServer(serviceSlug);
          } catch (e) {
            wixCodeApi.seo.setSeoStatusCode(404);
            setProps({ fitToContentHeight: true });
            return;
          }

          service = config.SEO.serviceResponse?.service;

          const referralInfo = getUrlQueryParamValue(
            wixCodeApi,
            BookingsQueryParams.REFERRAL,
          );

          flowAPI.bi?.updateDefaults({
            ...biDefaults,
            ...generateWidgetDefaults(appParams, platformAPIs, isEditor),
            serviceId: config.serviceDto?.id,
            service_id: config.serviceDto?.id,
            type: config.serviceDto?.type,
            referralInfo,
          });

          if (!config.serviceDto) {
            wixCodeApi.seo.setSeoStatusCode(404);
            setProps({ fitToContentHeight: true });
            return;
          }

          if (config.SEO.serviceResponse) {
            await wixCodeApi.seo.renderSEOTags({
              itemType: ITEM_TYPES.BOOKINGS_SERVICE,
              itemData: {
                serviceResponse: config.SEO.serviceResponse,
                bookingsPolicyDto: config.bookingPolicyDto,
              },
              seoData: config.SEO.serviceResponse?.service?.advancedSeoData,
            });
          }

          const isCourse = config.serviceDto?.type === ServiceType.COURSE;
          const serviceSchedule = config.SEO.serviceResponse?.schedules?.find(
            (schedule) => schedule.status === ScheduleStatus.CREATED,
          );
          const firstSessionStart = serviceSchedule?.firstSessionStart;
          const lastSessionEnd = serviceSchedule?.lastSessionEnd;
          const queryLocationId = wixCodeApi.location.query?.location;
          const selectedLocation = config.serviceDto?.info.locations?.find(
            (serviceLocation) =>
              serviceLocation.businessLocation?.id === queryLocationId,
          )
            ? queryLocationId
            : undefined;
          const initViewModels = initializeViewModels({
            config,
            t,
            experiments,
            selectedLocation,
            isSEO,
            isBookingCalendarInstalled,
            settings,
          });
          viewModel = initViewModels.viewModel;
          locationViewModel = initViewModels.locationViewModel;
          timezoneViewModel = initViewModels.timezoneViewModel;

          const getActionName = async () => {
            if (isCourse) {
              return isBookingFormInstalled
                ? ACTION_NAMES.NAVIGATE_TO_BOOKING_FORM
                : ACTION_NAMES.NAVIGATE_TO_CONTACT_FORM;
            } else {
              return isBookingCalendarInstalled
                ? ACTION_NAMES.NAVIGATE_TO_CALENDAR
                : undefined;
            }
          };

          navigateToCalendar = async (initiatedBy?: string) => {
            flowAPI.bi?.report(
              bookingsBookItClick({
                section: initiatedBy,
                actionName: await getActionName(),
                businessId: undefined,
              }),
            );

            set(viewModel, 'button.navigationInitiatedBy', initiatedBy);
            setProps({ viewModel });

            return handleNavigation({
              config,
              isPreview,
              flowAPI,
              locationId: locationViewModel.currentLocation,
              timezone: timezoneViewModel.viewTimezone,
              onNavigationFailed: async ({ failReasons }) => {
                const { sendNotification } = await import(
                  './controller-logic/sendNotification'
                );

                sendNotification({
                  appParams,
                  failReasons,
                  service: config.serviceDto!,
                  wixCodeApi,
                });
                flowAPI.bi?.report(
                  bookingsCantBookGroupsMessage({
                    widget_name: 'service_page',
                    referralInfo: 'service_page',
                    isPreview,
                    failReason: JSON.stringify(failReasons),
                  }),
                );
                showUserMessage();

                set(viewModel, 'button.navigationInitiatedBy', '');
                setProps({ viewModel });
              },
            });
          };

          getServiceSchedulingDataByLocation = (locationId: string) => {
            locationViewModel = schedulingLocationViewModelFactory({
              serviceInfoDto: config.serviceDto!.info,
              selectedLocation: locationId,
              t,
            });
            scheduleViewModel = {
              status: SchedulingSectionStatus.LOADING,
              isBookable: viewModel.body.isBookable,
            };
            setProps({
              locationViewModel,
              scheduleViewModel,
              fitToContentHeight: true,
            });
            if (
              !experiments.enabled('specs.bookings.react18ErrorInServicePge') ||
              !isSSR
            ) {
              getServiceSchedulingData({
                config,
                settings,
                httpClient,
                locationId,
                isIncreaseServicePageTimeBudget: true,
              })
                .then((schedule) => {
                  const changeTimezoneCallback = (
                    timezoneType: TimezoneType,
                  ) => {
                    timezoneViewModel =
                      schedulingTimezoneViewModelFactoryDeprecated({
                        businessInfo: config.businessInfo,
                        selectedTimezoneType: timezoneType,
                        isBookingCalendarInstalled,
                      });
                    viewModel = servicePageViewModelFactoryDeprecated({
                      config,
                      t,
                      experiments,
                      viewTimezone: timezoneViewModel.viewTimezone,
                      isSEO,
                      settings,
                    });
                    scheduleViewModel = schedulingSectionViewModelFactory({
                      isBookable: viewModel.body.isBookable,
                      catalogSessionsDto: schedule?.sessions,
                      businessInfo: config?.businessInfo,
                      viewTimezone: timezoneViewModel.viewTimezone,
                      isCourse,
                      firstSessionStart,
                      lastSessionEnd,
                      t,
                    });
                    setProps({
                      viewModel,
                      timezoneViewModel,
                      scheduleViewModel,
                    });
                  };
                  scheduleViewModel = schedulingSectionViewModelFactory({
                    isBookable: viewModel.body.isBookable,
                    catalogSessionsDto: schedule?.sessions,
                    businessInfo: config?.businessInfo,
                    viewTimezone: timezoneViewModel.viewTimezone,
                    isCourse,
                    firstSessionStart,
                    lastSessionEnd,
                    t,
                  });
                  setProps({
                    locationViewModel,
                    scheduleViewModel,
                    timezoneViewModel,
                    changeTimezoneCallback,
                  });
                })
                .catch((e) => {
                  reportError(e);
                  scheduleViewModel = {
                    status: SchedulingSectionStatus.FAILED,
                    isBookable: false,
                  };
                  setProps({
                    scheduleViewModel,
                  });
                });
            }
          };

          if (
            !isSSR ||
            experiments.enabled('specs.bookings.react18ErrorInServicePge')
          ) {
            getServiceSchedulingDataByLocation(
              locationViewModel.currentLocation,
            );
          }

          const trackingInfo = getTrackingInfoForServicePageLoads({
            service: config.serviceDto,
            businessName: config.businessInfo.name || '',
          });

          trackAnalytics({
            wixCodeApi,
            eventId: trackingInfo.eventId as any,
            payload: trackingInfo.payload,
          });
        } else {
          isEditorX =
            flowAPI.controllerConfig.config.style.styleParams.booleans
              .responsive;
          const courseAvailability =
            (settings.courseAvailability &&
              (settings.displayNumberOfSpots
                ? CourseAvailabilityDisplay.NUMBER_OF_SPOTS
                : CourseAvailabilityDisplay.AVAILABILITY)) ||
            undefined;
          viewModel = dummyViewModelFactoryDeprecated({
            t,
            isEditorX,
            courseAvailability,
          });
          const dummyBusinessInfo = {
            timeZone: 'UTC',
            regionalSettingsLocale: flowAPI.environment.language,
            dateRegionalSettingsLocale: flowAPI.environment.language,
          };
          scheduleViewModel = dummySchedulingViewModelDeprecated({
            experiments,
            t,
            businessInfo: dummyBusinessInfo,
            scheduleDays: settings.scheduleDays,
          });

          flowAPI.bi?.updateDefaults({
            ...biDefaults,
            ...generateWidgetDefaults(appParams, platformAPIs, isEditor),
          });
        }

        const reportPageLoaded = (params: BookingsServicesPageViewParams) =>
          flowAPI.bi?.report(bookingsServicesPageView(params));

        function changeLocationCallback(locationGuid: string) {
          if (!getServiceSchedulingDataByLocation) {
            return;
          }

          flowAPI.bi!.report(
            bookingsServicePageDrpodownChangeLocationSelection({
              locationGuid,
            }),
          );

          getServiceSchedulingDataByLocation(locationGuid);
        }

        const { userMessage, showUserMessage } = initUserMessage(setProps);

        setProps({
          navigateToCalendar,
          reportPageLoaded,
          scheduleViewModel,
          locationViewModel,
          changeLocationCallback,
          viewModel,
          userMessage,
          fitToContentHeight: true,
        });
      };
      if (
        experiments.enabled('specs.bookings.ServicePageV2Mapping') ||
        experiments.enabled('specs.bookings.ServiceV2ServicePage')
      ) {
        service = await initializeWidget({
          flowAPI,
          isBookingCalendarInstalled,
          isBookingFormInstalled,
          settings,
        });
      } else {
        await initWidget();
      }

      wixCodeApi.location.onChange(async () => {
        if (
          experiments.enabled('specs.bookings.ServicePageV2Mapping') ||
          experiments.enabled('specs.bookings.ServiceV2ServicePage')
        ) {
          await initializeWidget({
            flowAPI,
            isBookingCalendarInstalled,
            isBookingFormInstalled,
            settings,
          });
        } else {
          await initWidget();
        }
      });

      initSlotIntegration(service?.id!);
    },
    updateConfig($w, newConfig) {
      const updatedPublicData = newConfig.publicData.COMPONENT || {};
      const updatedSettings = getSettingsValues(
        updatedPublicData,
        settingsParams,
        { experiments },
      );
      if (settings.scheduleDays !== updatedSettings.scheduleDays) {
        const dummyBusinessInfo = {
          timeZone: 'UTC',
          regionalSettingsLocale: flowAPI.environment.language,
        };

        const scheduleViewModel = experiments.enabled(
          'specs.bookings.ServiceV2ServicePage',
        )
          ? dummySchedulingViewModel({
              t,
              businessInfo: dummyBusinessInfo,
              scheduleDays: updatedSettings.scheduleDays,
            })
          : dummySchedulingViewModelDeprecated({
              experiments,
              t,
              businessInfo: dummyBusinessInfo,
              scheduleDays: updatedSettings.scheduleDays,
            });

        setProps({
          scheduleViewModel,
        });
      }
      if (
        updatedSettings.displayNumberOfSpots !==
        prevSettings.displayNumberOfSpots
      ) {
        const courseAvailability =
          (updatedSettings.courseAvailability &&
            (updatedSettings.displayNumberOfSpots
              ? CourseAvailabilityDisplay.NUMBER_OF_SPOTS
              : CourseAvailabilityDisplay.AVAILABILITY)) ||
          undefined;
        const viewModel = experiments.enabled(
          'specs.bookings.ServiceV2ServicePage',
        )
          ? dummyViewModelFactory({
              t,
              isEditorX,
              courseAvailability,
            })
          : dummyViewModelFactoryDeprecated({
              t,
              isEditorX,
              courseAvailability,
            });
        setProps({
          viewModel,
        });
      }
      prevSettings = updatedSettings;
    },
  };
};

export default createController;
